import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ActionList,
  ActionListChildren,
  Box,
  Callout,
  Combobox,
  EmptyState,
  EmptyStateProps,
  Illustration,
  Input,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { CustomerWalletStatusType, type CustomerWalletStatus } from 'types/customers'

export interface CustomerWalletsProps {
  isCustomerVerified: boolean
  customerName: string
  searchQuery: string
  isSearching: boolean
  isLoading: boolean
  walletTableRows: ActionListChildren[]
  AddWalletButton: EmptyStateProps['PrimaryButton']
  onChangeSearchQuery: (searchQuery: string) => void
  statusFilterValues?: CustomerWalletStatus[]
  onStatusFilterChange?: (values: CustomerWalletStatus[]) => void
}

export const CustomerWallets = ({
  isCustomerVerified,
  customerName,
  searchQuery,
  isSearching,
  isLoading,
  walletTableRows,
  AddWalletButton,
  onChangeSearchQuery,
  statusFilterValues = [],
  onStatusFilterChange,
}: CustomerWalletsProps) => {
  const { t } = useTranslation()
  const hasWallets = !!walletTableRows?.length
  const hasStatusFilter = !!statusFilterValues.length

  const statusFilterItems = [
    { value: CustomerWalletStatusType.ACTIVE, leftText: t('customers.walletStatusActive') },
    { value: CustomerWalletStatusType.INACTIVE, leftText: t('customers.walletStatusInactive') },
    { value: CustomerWalletStatusType.TERMINATED, leftText: t('customers.walletStatusTerminated') },
  ]

  const statusSortOrder: CustomerWalletStatus[] = [
    CustomerWalletStatusType.ACTIVE,
    CustomerWalletStatusType.INACTIVE,
    CustomerWalletStatusType.TERMINATED,
  ]

  const CustomerWalletView = useMemo(() => {
    if (isLoading) {
      return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
    }

    if (!hasWallets) {
      return <NoSearchResults clearSearchCriteria={() => onChangeSearchQuery('')} />
    }

    return <ActionList id="customer-wallets-action-list" items={walletTableRows} />
  }, [isLoading, hasWallets, walletTableRows, onChangeSearchQuery])

  if (hasWallets || isSearching || isLoading || hasStatusFilter) {
    return (
      <>
        <Box paddingB={24} width="full" flex isFlexWrap gapX={12}>
          <Box className="lg:flex-auto lg:min-w-[200px]">
            <Input
              value={searchQuery}
              testId="customer-wallet-search-field"
              placeholder={t('customers.customerWalletSearchPlaceholder')}
              searchable
              onChange={event => onChangeSearchQuery(event.target.value)}
            />
          </Box>
          {onStatusFilterChange && (
            <Box className="w-auto" style={{ minWidth: '220px' }}>
              <Combobox
                id="customer-wallet-status-filter"
                testId="customer-wallet-status-filter"
                searchPlaceholder={t('customers.filterStatusLabel')}
                disabled={isLoading}
                items={statusFilterItems}
                selectPlaceholder={t('customers.filterStatusLabel')}
                checkedValues={statusFilterValues}
                onCheckedChange={value => {
                  const updatedSelection = statusFilterValues.includes(value as CustomerWalletStatus)
                    ? statusFilterValues.filter(status => status !== value)
                    : [...statusFilterValues, value as CustomerWalletStatus]
                  onStatusFilterChange(
                    [...updatedSelection].sort(
                      (statusA, statusB) =>
                        statusSortOrder.indexOf(statusA) - statusSortOrder.indexOf(statusB)
                    )
                  )
                }}
              />
            </Box>
          )}
        </Box>
        {!isCustomerVerified && (
          <Box paddingB={16}>
            <Callout variant="info">{t('customers.inactiveCustomerInfoMessage')}</Callout>
          </Box>
        )}
        {CustomerWalletView}
      </>
    )
  }

  if (!isCustomerVerified) {
    return (
      <EmptyState
        testId="customer-wallets-empty-state"
        title={t('customers.walletRestriction.customerNotVerifiedTitle')}
        description={t('customers.walletRestriction.customerNotVerifiedDescription')}
        Illustration={<Illustration name="NotVerified" />}
      />
    )
  }

  return (
    <EmptyState
      testId="customer-wallets-empty-state"
      title={t('customers.noCustomerWalletHeadline', { customerName })}
      description={t('customers.customerWalletDescription', { customerName })}
      Illustration={<Illustration name="NoData" />}
      PrimaryButton={AddWalletButton}
    />
  )
}
